import React, { createContext, useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { useLazyQuery } from "@apollo/client";
import * as Sentry from "@sentry/browser";
import { useToast } from "hooks";
import { AI_ROLE_BUILDER_STATUS, AI_ROLE_BUILDER_CONFIG, isTaskCompleted, isTaskFailed } from "constants/index";
import { GET_AI_ROLE_BUILDER_JOB_STATUS } from "graphql/queries";

const AIRoleBuilderContext = createContext(null);

/**
 * AIRoleBuilderProvider
 * Provides shared state for AI Role Builder across components
 */
const AIRoleBuilderProvider = ({
  children,
  config = AI_ROLE_BUILDER_CONFIG,
}) => {
  const { toast } = useToast();
  const [pendingJob, setPendingJob] = useState(null);
  const pollingIntervalRef = useRef(null);

  // GraphQL hook for polling
  const [getJobStatus] = useLazyQuery(GET_AI_ROLE_BUILDER_JOB_STATUS, {
    fetchPolicy: "network-only",
  });

  // Check localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(config.STORAGE_KEY);
    if (stored) {
      try {
        const job = JSON.parse(stored);
        setPendingJob(job);

        // Resume polling if job is still in progress
        if (!isTaskCompleted(job.status)) {
          startPolling(job.jobId);
        }
      } catch (error) {
        Sentry.captureException(error, {
          tags: {
            feature: "ai_role_builder",
            action: "parse_stored_job",
          },
          extra: {
            storedValue: stored,
            storageKey: config.STORAGE_KEY,
          },
        });

        localStorage.removeItem(config.STORAGE_KEY);
      }
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const submitJob = (mutationResult, resultKey = "submitAiRoleBuilderJob", successMessageId) => {
    // Extract job data from mutation result
    const { jobId } = mutationResult?.[resultKey] || {};

    if (!jobId) {
      cancelJob("AIRoleBuilder.Error", toast.error);
      return;
    }

    const job = {
      jobId,
      status: AI_ROLE_BUILDER_STATUS.INITIALIZED,
      submittedAt: Date.now(),
    };

    // Store job in localStorage
    localStorage.setItem(config.STORAGE_KEY, JSON.stringify(job));

    // Set in state and start polling
    setPendingJob(job);
    startPolling(jobId, successMessageId);
  };

  const startPolling = (jobId, successMessageId = "AIRoleBuilder.Success.Title") => {
    // Clear any existing interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    const poll = async () => {
      try {
        const { data, error } = await getJobStatus({ variables: { jobId } });

        if (error) {
          cancelJob("AIRoleBuilder.Error", toast.error);
          return;
        }

        const { status, ...rest } = data?.aiRoleBuilderJobStatus || {};

        if (!status) {
          cancelJob("AIRoleBuilder.Error", toast.error);
          return;
        }

        if (isTaskFailed(status)) {
          cancelJob("AIRoleBuilder.Error", toast.error);
          return;
        }

        // Check if we've reached a terminal status
        if (isTaskCompleted(status)) {
          // Stop polling
          clearInterval(pollingIntervalRef.current);
          localStorage.setItem(config.STORAGE_KEY, JSON.stringify({ status, ...rest }));
          setPendingJob({ status, ...rest });

          toast.success(<FormattedMessage id={successMessageId} />);
        } else {
          // Update job state with live status from API
          setPendingJob((prev) => {
            const updated = { ...prev, status, ...rest };
            localStorage.setItem(config.STORAGE_KEY, JSON.stringify(updated));

            return updated;
          });
        }
      } catch (error) {
        cancelJob("AIRoleBuilder.Error", toast.error);
      }
    };

    // Start polling
    pollingIntervalRef.current = setInterval(poll, config.POLLING_INTERVAL);

    // Poll immediately
    poll();
  };

  const cancelJob = (messageId = "AIRoleBuilder.Cancel.Success", type = toast.success) => {
    clearInterval(pollingIntervalRef.current);
    localStorage.removeItem(config.STORAGE_KEY);
    setPendingJob(null);

    type(<FormattedMessage id={messageId} />);
  };

  const dismissJob = () => {
    localStorage.removeItem(config.STORAGE_KEY);
    setPendingJob(null);
  };

  const value = {
    pendingJob,
    submitJob,
    cancelJob,
    dismissJob,
  };

  return <AIRoleBuilderContext.Provider value={value}>{children}</AIRoleBuilderContext.Provider>;
};

AIRoleBuilderProvider.propTypes = {
  children: PropTypes.node.isRequired,
  config: PropTypes.shape({
    STORAGE_KEY: PropTypes.string,
    POLLING_INTERVAL: PropTypes.number,
  }),
};

export default AIRoleBuilderProvider;

export { AIRoleBuilderContext };
